import axios, { type AxiosInstance } from "axios";
import { store } from "@/redux/store";
import { logout } from "@/redux/features/auth-slice";
import {
  isPublicAuthPath,
  isSessionExpiredPayload,
  redirectToLoginOnSessionExpired,
} from "@/utils/auth-session";

function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: "",
    withCredentials: true,
  });

  client.interceptors.request.use(
    (config) => {
      if (!(config.data instanceof FormData)) {
        config.headers.set("Content-Type", "application/json");
      } else {
        config.headers.delete("Content-Type");
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      const axiosError = error as {
        response?: {
          status?: number;
          headers?: Record<string, unknown>;
          data?: { error_code?: string; message?: string; detail?: string };
        };
        status?: number;
      };
      const status = axiosError.response?.status ?? axiosError.status;
      const data = axiosError.response?.data;

      const isAuthFailure =
        status === 401 ||
        String(data?.error_code ?? "").toUpperCase() === "AUTHENTICATION_ERROR";

      if (
        isAuthFailure &&
        isSessionExpiredPayload(data, axiosError.response?.headers) &&
        typeof window !== "undefined" &&
        !isPublicAuthPath(window.location.pathname)
      ) {
        store.dispatch(logout());
        redirectToLoginOnSessionExpired();
      }
      return Promise.reject(error);
    },
  );

  return client;
}

const apiClient = createApiClient();

export default apiClient;
